import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { HierarchyLevelType, SecurityClassification } from 'common/interfaces/hierarchy-entities';
import { getHierarchyLevelSliceName, getParentHierarchyEntityType } from 'client/views/explore/hierarchy/hierarchy.utils';
import { castDraft } from 'immer';
import { unionBy } from 'es-toolkit/compat';
import { t } from 'i18next';
import translations from 'client/internationalization/translation-map.json';
import { toast } from '@oliasoft-open-source/react-ui-library';

export const HIERARCHY_RESET_ACTION = 'hierarchy/reset';

export const hierarchyReset = () => ({
  type: HIERARCHY_RESET_ACTION,
});

export const HIERARCHY_ENTITIES_INFO: { [key: string]: { idProp: string; }; } = {
  '': {
    idProp: '',
  },
  [HierarchyLevelType.Archive]: {
    idProp: 'archiveid',
  },
  [HierarchyLevelType.Country]: {
    idProp: 'countryid',
  },
  [HierarchyLevelType.Field]: {
    idProp: 'fieldid',
  },
  [HierarchyLevelType.Site]: {
    idProp: 'siteid',
  },
  [HierarchyLevelType.Well]: {
    idProp: 'wellid',
  },
  [HierarchyLevelType.Wellbore]: {
    idProp: 'wellboreid',
  },
  [HierarchyLevelType.Design]: {
    idProp: 'designid',
  },
  [HierarchyLevelType.Node]: {
    idProp: 'nodeid',
  },
};

const upsertEntityArray = <T = any>(newItem: T, array: T[], predicate: (item: T) => boolean): T[] => {
  const existingArchiveIndex = array.findIndex(predicate);
  if (existingArchiveIndex < 0) {
    return [...array, newItem];
  }
  return [
    ...array.slice(0, existingArchiveIndex),
    newItem,
    ...array.slice(existingArchiveIndex + 1),
  ];
};

export const createHierarchyEntitySlice = <T>(entityType: HierarchyLevelType) => {
  const parentEntityType = getParentHierarchyEntityType(entityType);
  const parentEntityInfo = HIERARCHY_ENTITIES_INFO[parentEntityType || ''];
  const entityInfo = HIERARCHY_ENTITIES_INFO[entityType];
  const sliceName = getHierarchyLevelSliceName(entityType);

  const initialState = {
    isLoading: {} as { [parentId: string]: boolean; },
    items: [] as T[],
    lengths: {} as { [parentId: string]: number; },
    error: null as string | null,
    pages: {} as { [parentId: string]: number; },
  };

  const entity = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      itemsRequested: (state, action: PayloadAction<{ parentId?: string; }>) => {
        state.isLoading[action.payload.parentId || ''] = true;
        state.error = null;
      },
      itemsRecieved: (state, action: PayloadAction<[{ records: T[], totalRecords: number }, { parentId?: string; }]>) => {
        const [response, params] = action.payload;
        const { records, totalRecords } = response;
        const { parentId = '' } = params;
        if (records?.length) {
          // fix for the case when a new entity is added and put to the state, but then user requests the next page and some items can be duplicated
          state.items = unionBy(state.items, castDraft(records), (item => (item as any)[entityInfo.idProp]));
          const currentPage = state.pages[parentId] || 0;
          state.pages = {
            ...state.pages,
            [parentId]: currentPage + 1,
          };
        }
        state.lengths = {
          ...state.lengths,
          [parentId]: totalRecords,
        };
        state.isLoading[parentId] = false;
      },
      itemRecieved: (state, action: PayloadAction<T>) => {
        const parentId = (action.payload as any)[parentEntityInfo.idProp] || '';
        state.isLoading[parentId] = false;
        const itemId = (action.payload as any)[entityInfo.idProp];
        const existingItem = state.items.find((item: any) => item[entityInfo.idProp] === itemId);
        if (!existingItem) {
          state.lengths = { ...state.lengths, [parentId]: (state.lengths[parentId] || 0) + 1 };
        }
        state.items = castDraft(upsertEntityArray(action.payload, state.items as T[], (item: any) => item[entityInfo.idProp] === itemId));
      },
      itemsFailed: (state, action: PayloadAction<{ parentId?: string; message: string; }>) => {
        state.isLoading[action.payload.parentId || ''] = false;
        state.error = action.payload.message;
        if (action.payload.message.includes('403')) {
          toast({
            message: {
              type: 'Error',
              icon: true,
              content: t(translations.authorization_notAuthorized),
            },
          });
        }
      },
      itemDeleteRecieved: (state, action: PayloadAction<string>) => {
        state.isLoading[''] = false;
        const itemIndex = state.items.findIndex(i => (i as any)[entityInfo.idProp] === action.payload);
        const item = state.items[itemIndex];
        state.items.splice(itemIndex, 1);
        const parentId = (item as any)[parentEntityInfo.idProp];
        if (parentId) {
          state.lengths[parentId] -= 1;
        }
      },
      itemReset: (state) => {
        state.items = [];
        state.pages = {};
        state.lengths = {};
      },
      classificationLevelUpdated: (state, action: PayloadAction<{ id: string, classificationlevel: SecurityClassification }>) => {
        const { id, classificationlevel } = action.payload;
        state.items = state.items.map((item: any) => (item[entityInfo.idProp] !== id ? item : {
          ...item,
          classificationlevel,
        }));
      },
      itemUpdated: (state, action: PayloadAction<any>) => {
        const itemIndex = state.items.findIndex(i => (i as any)[entityInfo.idProp] === action.payload[entityInfo.idProp]);
        state.items[itemIndex] = action.payload;
      },
    },
    extraReducers: (builder) => {
      builder
        .addMatcher(
          (action) => action.type === HIERARCHY_RESET_ACTION,
          () => {
            return initialState;
          },
        );
    },
  });
  return entity;
};
